﻿import { useEffect, useMemo, useRef, useState } from "react";

import { AiRoadmapProjectScreen } from "./AiRoadmapProjectScreen";
import { AiRoadmapPrioritizedScreen } from "./AiRoadmapPrioritizedScreen";
import { AiRoadmapRoadmapSuggestionScreen } from "./AiRoadmapRoadmapSuggestionScreen";
import { AiRoadmapFinalReviewScreen } from "./AiRoadmapFinalReviewScreen";
import { AiRoadmapReviewScreen } from "./AiRoadmapReviewScreen";
import { AiRoadmapScoringScreen } from "./AiRoadmapScoringScreen";
import { fetchRoadmapFinalReview } from "../api/roadmapClient";
import { useI18n } from "../i18n";
import { PageHeader, PageShell, ProgressIndicator, Tabs } from "./roadmap/DesignSystem";

type RoadmapTab = "project" | "review" | "final-review" | "scoring" | "prioritized" | "roadmap";

function tabFromPath(pathname: string): RoadmapTab {
  if (pathname === "/atlas" || pathname.startsWith("/atlas/")) return "prioritized";
  if (pathname.startsWith("/ai_roadmap/final-review")) return "final-review";
  if (pathname.startsWith("/ai_roadmap/review")) return "review";
  if (pathname.startsWith("/ai_roadmap/scoring")) return "scoring";
  if (pathname.startsWith("/ai_roadmap/prioritized")) return "prioritized";
  if (pathname.startsWith("/ai_roadmap/roadmap")) return "roadmap";
  return "project";
}

export function AiRoadmapScreen(): JSX.Element {
  const { t } = useI18n();
  const [pathname, setPathname] = useState(() => window.location.pathname);
  const [activeProjectId, setActiveProjectId] = useState(() => {
    return localStorage.getItem("studio.aiRoadmap.activeProject") ?? "";
  });
  const [firstReviewValidated, setFirstReviewValidated] = useState(false);
  const [finalReviewValidated, setFinalReviewValidated] = useState(false);
  const tabsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handlePop = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  useEffect(() => {
    const handleProjectChange = () => {
      setActiveProjectId(localStorage.getItem("studio.aiRoadmap.activeProject") ?? "");
    };
    window.addEventListener("roadmap:project-changed", handleProjectChange);
    window.addEventListener("roadmap:first-review-validated", handleProjectChange);
    return () => {
      window.removeEventListener("roadmap:project-changed", handleProjectChange);
      window.removeEventListener("roadmap:first-review-validated", handleProjectChange);
    };
  }, []);

  useEffect(() => {
    const search = window.location.search;
    if (pathname === "/atlas") {
      window.history.replaceState({}, "", `/ai_roadmap/prioritized${search}`);
      setPathname("/ai_roadmap/prioritized");
      return;
    }
    if (pathname === "/ai_roadmap") {
      window.history.replaceState({}, "", `/ai_roadmap/project${search}`);
      setPathname("/ai_roadmap/project");
    }
  }, [pathname]);

  useEffect(() => {
    const tabsEl = tabsRef.current;
    if (!tabsEl) return;

    const updateTabsHeight = () => {
      const height = tabsEl.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--atlas-tabs-height", `${height}px`);
    };

    updateTabsHeight();

    const observer = new ResizeObserver(updateTabsHeight);
    observer.observe(tabsEl);
    window.addEventListener("resize", updateTabsHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateTabsHeight);
    };
  }, []);

  useEffect(() => {
    const projectId = activeProjectId;
    if (!projectId) {
      setFirstReviewValidated(false);
      setFinalReviewValidated(false);
      return;
    }
    const firstReviewKey = `studio.aiRoadmap.firstReviewValidated.${projectId}`;
    setFirstReviewValidated(Boolean(localStorage.getItem(firstReviewKey)));
    fetchRoadmapFinalReview(projectId)
      .then((review) => {
        const normalized = review.status?.trim().toLowerCase();
        setFinalReviewValidated(normalized === "validated" || normalized === "approved");
      })
      .catch(() => {
        setFinalReviewValidated(false);
      });
  }, [activeProjectId]);

  const activeTab = useMemo(() => tabFromPath(pathname), [pathname]);

  const roadmapTabs = [
    { id: "project", label: t("atlas.tabs.project"), href: "/ai_roadmap/project" },
    { id: "review", label: t("atlas.tabs.firstReview"), href: "/ai_roadmap/review" },
    { id: "final-review", label: t("atlas.tabs.finalReview"), href: "/ai_roadmap/final-review" },
    { id: "scoring", label: t("atlas.tabs.scoring"), href: "/ai_roadmap/scoring" },
    { id: "prioritized", label: t("atlas.tabs.applicationsList"), href: "/ai_roadmap/prioritized" },
    { id: "roadmap", label: t("atlas.tabs.roadmap"), href: "/ai_roadmap/roadmap" },
  ];

  const handleTabChange = (nextTabId: string) => {
    const nextTab = (
      nextTabId === "review" ||
      nextTabId === "final-review" ||
      nextTabId === "scoring" ||
      nextTabId === "prioritized" ||
      nextTabId === "roadmap"
        ? nextTabId
        : "project"
    ) as RoadmapTab;
    const href = roadmapTabs.find((tab) => tab.id === nextTab)?.href ?? "/ai_roadmap/project";
    if (href !== window.location.pathname) {
      window.history.pushState({}, "", href);
      setPathname(href);
    }
  };

  const tabsWithStatus: Array<{ id: string; label: string; status?: "pending" | "validated" }> = roadmapTabs.map(
    (tab) => {
      if (tab.id === "review") {
        return { id: tab.id, label: tab.label, status: firstReviewValidated ? "validated" : "pending" };
      }
      if (tab.id === "final-review") {
        return { id: tab.id, label: tab.label, status: finalReviewValidated ? "validated" : "pending" };
      }
      return { id: tab.id, label: tab.label, status: "pending" };
    },
  );

  const progressSteps = [
    { label: t("atlas.progress.firstReview"), validated: firstReviewValidated },
    { label: t("atlas.progress.finalReview"), validated: finalReviewValidated },
  ];

  return (
    <PageShell>
      <div className="ai-roadmap-screen">
        <div
          ref={tabsRef}
          className="ai-roadmap-screen__sticky sticky z-40 bg-card pb-4 shadow-soft"
          style={{ top: "var(--roadmap-sticky-top, calc(var(--app-header-height) + 1.5rem))" }}
        >
          <PageHeader
            title="Atlas"
            description="Consolidez la discovery, structurez le portefeuille de use cases, puis sortez une roadmap executive defendable pour les grands comptes."
            actions={
              <div className="ai-roadmap-screen__tabs-wrap">
                <Tabs tabs={tabsWithStatus} activeId={activeTab} onChange={handleTabChange} />
              </div>
            }
          />
          <div className="ai-roadmap-screen__progress mt-2 flex items-center justify-between gap-3">
            <ProgressIndicator steps={progressSteps} />
          </div>
        </div>

        <div className="ai-roadmap-screen__content">
          {activeTab === "review" ? (
            <AiRoadmapReviewScreen />
          ) : activeTab === "final-review" ? (
            <AiRoadmapFinalReviewScreen />
          ) : activeTab === "scoring" ? (
            <AiRoadmapScoringScreen />
          ) : activeTab === "prioritized" ? (
            <AiRoadmapPrioritizedScreen />
          ) : activeTab === "roadmap" ? (
            <AiRoadmapRoadmapSuggestionScreen />
          ) : (
            <AiRoadmapProjectScreen hideHeader />
          )}
        </div>
      </div>
    </PageShell>
  );
}
